import React, { useEffect, useRef, useState } from "react";
import { Check, ChevronDown, Filter } from "lucide-react";
import { C, filterOptionStyle, outlineBtnStyle } from "../styles";
import type { PeriodType, SortType } from "../types";

interface FilterMenuProps {
  sort: SortType;
  period: PeriodType;
  onSortChange: (s: SortType) => void;
  onPeriodChange: (p: PeriodType) => void;
}

const SORT_OPTIONS: ReadonlyArray<[SortType, string]> = [
  ["newest", "Newest first"],
  ["oldest", "Oldest first"],
  ["net-high", "Net total, highest first"],
  ["net-low", "Net total, lowest first"],
];

const PERIOD_OPTIONS: ReadonlyArray<[PeriodType, string]> = [
  ["all", "All periods"],
  ["this-year", "This year"],
  ["last-quarter", "Last 3 months"],
  ["this-month", "This month"],
];

export default function FilterMenu({
  sort,
  period,
  onSortChange,
  onPeriodChange,
}: FilterMenuProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  return (
    <div
      ref={ref}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        position: "relative",
      }}
    >
      <button onClick={() => setOpen((o) => !o)} style={outlineBtnStyle}>
        <Filter size={12} /> Filter <ChevronDown size={10} />
      </button>
      {open && (
        <div
          style={{
            position: "absolute",
            top: 40,
            right: 0,
            background: C.card,
            border: "1px solid " + C.line,
            borderRadius: 10,
            boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
            padding: "10px 0",
            minWidth: 220,
            zIndex: 100,
          }}
        >
          <div
            style={{
              padding: "4px 14px 8px",
              fontSize: 10,
              fontWeight: 700,
              color: C.ink,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
            }}
          >
            Sort by
          </div>
          {SORT_OPTIONS.map(([value, label]) => (
            <button
              key={value}
              onClick={() => onSortChange(value)}
              style={filterOptionStyle}
            >
              <span>{label}</span>
              {sort === value && (
                <Check size={14} style={{ color: C.brand }} strokeWidth={3} />
              )}
            </button>
          ))}
          <div style={{ height: 1, background: C.line, margin: "6px 0" }} />
          <div
            style={{
              padding: "4px 14px 8px",
              fontSize: 10,
              fontWeight: 700,
              color: C.ink,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
            }}
          >
            Pay period
          </div>
          {PERIOD_OPTIONS.map(([value, label]) => (
            <button
              key={value}
              onClick={() => onPeriodChange(value)}
              style={filterOptionStyle}
            >
              <span>{label}</span>
              {period === value && (
                <Check size={14} style={{ color: C.brand }} strokeWidth={3} />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}